import { EventType } from "@ac-expo/api-types";
import {
  DISCOUNT_CODE_NAME_PATTERN,
  getDatesBetween,
  getEventEndDate,
  normalizeDiscountCodeName,
} from "@ac-expo/utils";
import { isAfter } from "date-fns";
import { DefaultValues } from "react-hook-form";
import { z } from "zod";
import { createDiscountSchema, defaultCreateDiscountValues } from "./create-discount.schema";

export const DISCOUNT_CODE_NAME_PATTERN_MESSAGE =
  "Le code ne peut contenir que des lettres et des chiffres";
export const DISCOUNT_CODE_NAME_MAX_LENGTH = 15;
export const MAX_USES_PER_VISITOR_MESSAGE = "La limite par visiteur doit être supérieure à 0";
export const MAX_USES_PER_VISITOR_ABOVE_TOTAL_MESSAGE =
  "La limite par visiteur ne peut pas dépasser la quantité totale";

// A finite limit or "Infini" (unlimited), shared by the total, the per-visitor limit and the
// per-prestation sub-quotas.
export const discountCodeLimitSchema = (message: string) =>
  z.coerce
    .number({ invalid_type_error: message })
    .int(message)
    .gt(0, message)
    .or(z.literal("Infini"));

// Indexes of every label already seen earlier in the list (the first occurrence is kept).
export const findDuplicatedDiscountCodeIndexes = (names: string[]): number[] => {
  const seen = new Set<string>();
  const duplicated: number[] = [];
  names.forEach((name, i) => {
    const key = normalizeDiscountCodeName(name);
    if (seen.has(key)) duplicated.push(i);
    else seen.add(key);
  });
  return duplicated;
};

// Marks every repeated label of a multi-code form (superRefine body shared by the
// ticket, badge and animation schemas — the message names the owning entity).
export const addDuplicatedDiscountCodeIssues = (
  discountCodes: { name: string }[],
  ctx: z.RefinementCtx,
  message: string
): void => {
  findDuplicatedDiscountCodeIndexes(discountCodes.map((dc) => dc.name)).forEach((i) => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ["discountCodes", i, "name"],
    });
  });
};

// Base object: the label is only required here. The format rule lives in
// `withDiscountCodeNameRule` so that schemas merging this object can apply it too.
export const discountCodeBaseSchema = z.object({
  id: z.string().optional(),
  name: z.string().trim().min(1, "Le nom du code est requis"),
  isCumulable: z.boolean(),
  isQuantityInfinite: z.boolean().default(false),
  quantity: discountCodeLimitSchema("La quantité doit être supérieure à 0"),
  maxUsesPerVisitor: discountCodeLimitSchema(MAX_USES_PER_VISITOR_MESSAGE).default("Infini"),
  validityDates: z
    .array(
      z
        .date({
          required_error: "La date de validité est requise",
        })
        .refine((date) => !isNaN(date.getTime()), "Date de validité invalide"),
    )
    .min(1, "Les dates de validité sont requises"),
  discount: createDiscountSchema,
});

// Letters and digits only, uppercased, 15 max — for new codes only. An existing code (with an
// id) keeps its stored label untouched so a legacy label never blocks the edit of its parent;
// a rename of an existing code is validated by the backend.
export const withDiscountCodeNameRule = <T extends z.ZodType<{ id?: string; name: string }>>(
  schema: T,
) =>
  schema
    .superRefine((data, ctx) => {
      if (data.id) return;
      const name = normalizeDiscountCodeName(data.name);
      if (name.length > DISCOUNT_CODE_NAME_MAX_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_big,
          maximum: DISCOUNT_CODE_NAME_MAX_LENGTH,
          type: "string",
          inclusive: true,
          message: `Le nom du code ne doit pas dépasser ${DISCOUNT_CODE_NAME_MAX_LENGTH} caractères`,
          path: ["name"],
        });
      }
      if (!DISCOUNT_CODE_NAME_PATTERN.test(name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: DISCOUNT_CODE_NAME_PATTERN_MESSAGE,
          path: ["name"],
        });
      }
    })
    .transform((data): z.output<T> =>
      data.id ? data : { ...data, name: normalizeDiscountCodeName(data.name) },
    );

// When both limits are finite, one visitor cannot be allowed more uses than the code has.
type DiscountCodeLimits = {
  quantity?: number | "Infini";
  maxUsesPerVisitor?: number | "Infini";
};

export const withDiscountCodeLimitsRule = <T extends z.ZodTypeAny>(
  schema: T,
): z.ZodEffects<T, z.output<T>, z.input<T>> =>
  schema.superRefine((data, ctx) => {
    const { quantity, maxUsesPerVisitor } = data as DiscountCodeLimits;
    if (
      typeof quantity === "number" &&
      typeof maxUsesPerVisitor === "number" &&
      maxUsesPerVisitor > quantity
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: MAX_USES_PER_VISITOR_ABOVE_TOTAL_MESSAGE,
        path: ["maxUsesPerVisitor"],
      });
    }
  }) as z.ZodEffects<T, z.output<T>, z.input<T>>;

export const createDiscountCodeSchema = withDiscountCodeLimitsRule(
  withDiscountCodeNameRule(discountCodeBaseSchema),
);

export type CreateDiscountCode = z.infer<typeof createDiscountCodeSchema>;

export const defaultCreateDiscountCodeValues: DefaultValues<CreateDiscountCode> = {
  name: "",
  isCumulable: false,
  quantity: "Infini",
  maxUsesPerVisitor: "Infini",
  validityDates: [],
  discount: defaultCreateDiscountValues,
};

export const getDefaultCreateDiscountCodeValues = (
  dates: EventType["dates"],
): DefaultValues<CreateDiscountCode> => {
  return {
    ...defaultCreateDiscountCodeValues,
    validityDates:
      dates && isAfter(dates?.schedules[dates.schedules.length - 1].toDate, new Date())
        ? getDatesBetween(new Date(), getEventEndDate(dates))
        : [],
  };
};
